import { useFeatureFlags } from '@opallabs/api'
import { Theme } from '@opallabs/gem'
import { DefaultQueryClientProvider } from '@opallabs/higgs'
import { MessageKeys } from '@opallabs/hooks'
import { Tooltip, Button, Icon } from '@opallabs/lapidary'
import { AnalyticsLayer } from '@opallabs/quanta'
import UserToken from '../UserToken'
import GlobalSearchButton from './GlobalSearchButton'
import * as styles from './styles'

export type UserMenuProps = {
  hasUnseenNotification: boolean
  isExportPopoverOpen: boolean
  isViewOnlyRole: boolean
  onClickAvatar: () => void
  onExportPopoverConfirm: () => void
  onShare?: () => void
  shouldEnableAvatar?: boolean
  shouldEnableJump?: boolean
  shouldEnableResources?: boolean
  shouldEnableSharing?: boolean
  theme: Theme
}

const UserMenuComponent: React.FC<UserMenuProps> = ({
  hasUnseenNotification,
  isExportPopoverOpen,
  isViewOnlyRole,
  onClickAvatar,
  onExportPopoverConfirm,
  onShare,
  shouldEnableAvatar = true,
  shouldEnableJump = true,
  shouldEnableResources = true,
  shouldEnableSharing = false,
  theme = 'dark',
}) => {
  const { data: features } = useFeatureFlags()

  const handleGemOrIntercom = () => {
    window.parent.postMessage(
      {
        message: features?.gem_copilot
          ? MessageKeys.toggleCopilot
          : MessageKeys.showIntercom,
      },
      '*'
    )
  }

  return (
    <AnalyticsLayer name="User Menu">
      <div css={styles.userMenu} role="group">
        {shouldEnableSharing && (
          <Button
            className="user-menu-item"
            onClick={onShare}
            name="share"
            buttonText="Share"
            // `shouldEnableSharing` only renders this `<Button>` within Supernova colors, where it's either against a
            // light background (where the light theme is fine) or a dark background that doesn't work well with Gem's
            // contrast balances. The rest of the components used within `<UserMenu>` work fine with both themes, but
            // this is only usable with the light theme.
            theme="light"
            variant="primary"
          />
        )}

        {shouldEnableJump && !isViewOnlyRole && (
          <div css={styles.userMenuItem(theme)} className="user-menu-item">
            <GlobalSearchButton theme={theme} />
          </div>
        )}

        {shouldEnableResources && (
          <Tooltip
            content={features?.gem_copilot ? 'Open Gem' : 'Help & Support'}
            legacyTheme={theme}
            zIndex={9999}
          >
            <Button
              css={styles.userMenuItem(theme)}
              className="user-menu-item resources-center-button"
              name={features?.gem_copilot ? 'Open Gem' : 'Help & Support'}
              onClick={handleGemOrIntercom}
              icon={
                <Icon
                  ariaHidden={true}
                  glyph={features?.gem_copilot ? 'gem-ai' : 'gem-question-mark'}
                  width={24}
                  height={24}
                  color="#a0aeb9"
                />
              }
              variant="ghost"
              theme={theme}
              ariaLabel={
                features?.gem_copilot
                  ? 'Open Gem'
                  : 'Open Help & Support Center'
              }
            />
          </Tooltip>
        )}

        {shouldEnableAvatar && (
          <div
            css={styles.userMenuItem(theme)}
            className="user-menu-item avatar-wrapper"
          >
            <UserToken
              hasUnseenNotification={hasUnseenNotification}
              isExportPopoverOpen={isExportPopoverOpen}
              onExportPopoverConfirm={onExportPopoverConfirm}
              onClick={onClickAvatar}
              theme={theme}
              avatarOverrideProps={{
                styleOverrides: styles.userAvatar,
              }}
            />
          </div>
        )}
      </div>
    </AnalyticsLayer>
  )
}

const UserMenu = (props: UserMenuProps) => (
  <DefaultQueryClientProvider>
    <UserMenuComponent {...props} />
  </DefaultQueryClientProvider>
)

export default UserMenu
